'use client';

import React, { useEffect, useRef, useState, useTransition } from 'react';

import { AlertCircle, Check, Gift, Loader2, Tag, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { CurrencyIcon } from '@kit/ui/currency-icon';
import { Input } from '@kit/ui/input';
import { cn } from '@kit/ui/utils';

import {
  applyClassDiscountCodeAction,
  applyDiscountCodeAction,
  removeClassDiscountCodeAction,
  removeDiscountCodeAction,
  validateDiscountCodeAction,
} from '../server/actions/discount-code-actions';

export type AppliedDiscount = {
  code: string;
  name: string;
  amountOff: number;
  /** How the code is expressed. Present on the preview path (validate_discount_code
   *  returns it); absent on the apply paths and for perks, where the callers only
   *  have the resolved amount. Lets a surface show "20% off" instead of a raw
   *  riyal figure — the amount still carries the exact money. */
  discountType?: 'percent' | 'fixed';
  /** The code's face value: percent points when discountType is 'percent',
   *  otherwise a currency amount. NOT the amount actually taken off (that is
   *  amountOff, which the server caps). */
  discountValue?: number;
  /** True when the discount is a membership perk (auto-applied, not removable by customer). */
  isPerk?: boolean;
};

/** A tenant-advertised (public) code the customer can pick without typing. */
export type SuggestedDiscountCode = {
  code: string;
  name: string;
  discountType: 'percent' | 'fixed';
  discountValue: number;
};

type Mode =
  | {
      kind: 'preview';
      accountId: string;
      // Slot bookings pass a real date/time; package purchases omit both (no
      // slot). The server treats absent date/time as the package context.
      bookingDate?: string;
      startTime?: string;
      // Slot end time (HH:MM). Sent to the server so it can enforce that the
      // *entire* booking duration falls inside the discount's hour window —
      // not just the start. Optional for package purchases (no duration) and
      // legacy callers that haven't computed it yet.
      endTime?: string;
      draftTotal: number;
      templateId?: string;
      bookingType?: string;
      // The booking_packages.id being purchased. Sent on the package-checkout
      // surface so a code scoped to a specific package (scope_package_id) can
      // be matched in the preview — otherwise it always previews as invalid.
      // Omitted for slot bookings (no package).
      packageId?: string;
      // Active location on the slot surface — lets a location-scoped code
      // preview as valid there (without it such codes always preview as
      // invalid). Package checkout omits it (the server resolves the
      // package's own location); redemption re-checks authoritatively.
      locationId?: string;
      // The membership_plans.id being purchased. Sent on the membership
      // checkout so only membership codes (optionally scoped to this plan)
      // preview as valid. The server resolves the location from the plan.
      membershipPlanId?: string;
      recipientUserId?: string;
    }
  | {
      kind: 'apply';
      bookingId: string;
    }
  | {
      // Class enrollment variant — applies the discount to a single
      // class_participants row (participant-scoped court_booking_addons row).
      kind: 'apply-class';
      participantId: string;
    };

interface DiscountCodeInputProps {
  mode: Mode;
  applied?: AppliedDiscount | null;
  onApplied?: (discount: AppliedDiscount) => void;
  onRemoved?: () => void;
  defaultOpen?: boolean;
  className?: string;
  /**
   * Whether to show the "bookings with a discount code applied cannot be
   * cancelled online" note. Defaults to true. Package checkout hides it because
   * the cancellation rule doesn't apply to package purchases.
   */
  showCancelNote?: boolean;
  /**
   * Tenant-advertised public codes for this surface — rendered as one-tap
   * buttons above the input so customers don't have to type. Selection is
   * still validated server-side like a typed code.
   */
  suggestedCodes?: SuggestedDiscountCode[];
  /**
   * When exactly ONE suggested code exists, silently try to apply it on mount
   * (auto-select). A failed attempt (e.g. usage cap reached, day/hour window)
   * shows no error — the customer can still type another code.
   */
  autoApplySingle?: boolean;
  /**
   * Extra context that should re-arm the auto-apply after the parent clears
   * `applied`. The guard key already covers every preview-mode field; pass
   * anything else the parent resets the applied discount on (selected court,
   * class, add-ons total) so the same code is silently re-applied on that
   * change instead of being demoted to a tap-to-apply suggestion.
   */
  autoApplyContextKey?: string;
}

export function DiscountCodeInput({
  mode,
  applied,
  onApplied,
  onRemoved,
  defaultOpen = false,
  className,
  showCancelNote = true,
  suggestedCodes = [],
  autoApplySingle = false,
  autoApplyContextKey = '',
}: DiscountCodeInputProps) {
  const { t } = useTranslation('addons');
  const [isOpen, setIsOpen] = useState(defaultOpen || !!applied);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const recipientUserId = mode.kind === 'preview' ? mode.recipientUserId : undefined;
  const latestRecipient = useRef(recipientUserId);
  useEffect(() => {
    latestRecipient.current = recipientUserId;
  }, [recipientUserId]);
  // Which suggested code an auto-apply was already attempted for — never retry
  // the same one (a failed silent attempt must not loop).
  const autoTriedRef = useRef<string | null>(null);

  const discountErrorMessage = (message?: string | null) => {
    if (message === 'This code is not valid on the selected booking date') {
      return t('customerDiscount.bookingDateMismatch');
    }
    if (message === 'This code is not available for this customer') {
      return t('customerDiscount.eligibilityMismatch');
    }
    return message || t('customerDiscount.invalidOrExpired');
  };

  const applyCode = (rawCode: string, opts: { silent?: boolean } = {}) => {
    const trimmed = rawCode.trim();

    if (!trimmed) {
      if (!opts.silent) setError(t('customerDiscount.enterCodePrompt'));
      return;
    }

    startTransition(async () => {
      try {
        if (mode.kind === 'preview') {
          const result = await validateDiscountCodeAction({
            code: trimmed,
            accountId: mode.accountId,
            bookingDate: mode.bookingDate,
            startTime: mode.startTime,
            endTime: mode.endTime,
            draftTotal: mode.draftTotal,
            templateId: mode.templateId,
            bookingType: mode.bookingType,
            packageId: mode.packageId,
            locationId: mode.locationId,
            membershipPlanId: mode.membershipPlanId,
            ...(mode.recipientUserId
              ? { recipientUserId: mode.recipientUserId }
              : {}),
          });

          if (latestRecipient.current !== mode.recipientUserId) return;

          if (!result || !('valid' in result) || result.valid !== true) {
            if (!opts.silent) {
              const reason = result && 'reason' in result ? result.reason : null;
              setError(discountErrorMessage(reason));
            }
            return;
          }

          onApplied?.({
            code: result.code,
            name: result.name,
            amountOff: Number(result.preview_amount),
            discountType: result.discount_type,
            discountValue: Number(result.discount_value),
          });
          setCode('');
        } else {
          const result =
            mode.kind === 'apply-class'
              ? await applyClassDiscountCodeAction({
                  code: trimmed,
                  participantId: mode.participantId,
                })
              : await applyDiscountCodeAction({
                  code: trimmed,
                  bookingId: mode.bookingId,
                });

          const data = (result?.data ?? {}) as Record<string, unknown>;

          onApplied?.({
            code: String(data.code ?? trimmed.toUpperCase()),
            name: String(data.name ?? trimmed.toUpperCase()),
            amountOff: Number(data.amount_off ?? 0),
          });
          setCode('');
        }
      } catch (err) {
        if (!opts.silent) {
          setError(
            discountErrorMessage(err instanceof Error ? err.message : null),
          );
        }
      }
    });
  };

  const handleApply = () => {
    setError(null);
    applyCode(code);
  };

  // Auto-select the single advertised code: fires a one-shot, silent apply so
  // the customer lands on the discounted price without typing. useEffect is
  // justified — this reacts to externally-computed props (the suggestion list)
  // with a server round-trip, not derivable during render.
  //
  // The ref stores the LAST attempted key (full preview context + code), not
  // an "ever tried" set: a failed silent attempt never retries while the
  // context is unchanged (no loop — the effect re-runs with the same key and
  // bails), but any context change (different package, template, date, time,
  // duration, price, location, or caller context such as the selected court)
  // produces a new key and re-attempts — so switching a selection re-applies
  // the discount after the parent cleared the applied state. The key MUST
  // cover everything the parent resets `applied` on: a reset without a key
  // change leaves the code stranded as a tap-to-apply suggestion.
  const autoApplyKey =
    suggestedCodes.length === 1
      ? mode.kind === 'preview'
        ? [
            mode.packageId ?? '',
            mode.templateId ?? '',
            mode.bookingDate ?? '',
            mode.startTime ?? '',
            mode.endTime ?? '',
            mode.locationId ?? '',
            mode.membershipPlanId ?? '',
            mode.recipientUserId ?? '',
            String(mode.draftTotal),
            autoApplyContextKey,
            suggestedCodes[0]!.code,
          ].join(':')
        : [autoApplyContextKey, suggestedCodes[0]!.code].join(':')
      : null;

  useEffect(() => {
    if (!autoApplySingle || applied || !autoApplyKey) return;
    if (autoTriedRef.current === autoApplyKey) return;
    autoTriedRef.current = autoApplyKey;
    applyCode(suggestedCodes[0]!.code, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applyCode identity changes every render; the last-key ref guard makes re-runs no-ops
  }, [autoApplySingle, applied, autoApplyKey]);

  // While a lone code is being auto-applied, the amber suggestion chip would
  // render for the duration of the server round-trip and then be replaced by
  // the green applied chip — a visible yellow flash on every open. Suppress it
  // for exactly that case: auto-apply is armed, there is one code, and its
  // attempt is pending or about to fire. A code that FAILED silently still
  // shows (autoTriedRef is set but isPending is false and applied stays null),
  // so the customer can tap it manually.
  const autoApplyPending =
    autoApplySingle &&
    suggestedCodes.length === 1 &&
    !applied &&
    !!autoApplyKey &&
    (isPending || autoTriedRef.current !== autoApplyKey);

  const suggestionButtons =
    suggestedCodes.length > 0 && !applied && !autoApplyPending ? (
      <div className="space-y-1.5" data-test="suggested-discount-codes">
        <p className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
          <Gift className="h-3.5 w-3.5" />
          {t('customerDiscount.offers.pickOne')}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {suggestedCodes.map((s) => (
            <button
              key={s.code}
              type="button"
              disabled={isPending}
              onClick={() => {
                setIsOpen(true);
                setError(null);
                setCode(s.code);
                applyCode(s.code);
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 font-mono text-xs font-semibold text-amber-900 transition-colors hover:bg-amber-100 disabled:opacity-50 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/60"
              data-test="suggested-discount-code"
            >
              {s.code}
              <span className="font-sans font-normal text-amber-700 dark:text-amber-300">
                {s.discountType === 'percent'
                  ? t('customerDiscount.offers.percentOff', {
                      value: Number(s.discountValue).toFixed(0),
                    })
                  : t('customerDiscount.offers.amountOff', {
                      value: Number(s.discountValue).toFixed(0),
                    })}
              </span>
            </button>
          ))}
        </div>
      </div>
    ) : null;

  const handleRemove = () => {
    setError(null);
    startTransition(async () => {
      try {
        if (mode.kind === 'apply') {
          await removeDiscountCodeAction({ bookingId: mode.bookingId });
        } else if (mode.kind === 'apply-class') {
          await removeClassDiscountCodeAction({
            participantId: mode.participantId,
          });
        }
        onRemoved?.();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t('customerDiscount.couldNotRemove'),
        );
      }
    });
  };

  if (applied) {
    return (
      <div
        className={cn(
          'rounded-lg border border-green-200 bg-green-50 px-3 py-1',
          className,
        )}
        data-test="applied-discount-code"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <Check className="h-4 w-4 flex-shrink-0 text-green-600" />
            {/* Code and magnitude sit on ONE wrapping row so the chip stays a
                single line at normal widths. flex-wrap (not nowrap) keeps a
                long code + perk label from overflowing on a narrow screen —
                it drops to a second line only when it genuinely cannot fit.

                Magnitude describes the code the way the customer thinks of it:
                a percent code reads "20% off", not a riyal figure. The exact
                money is already stated line-by-line in the surrounding
                Subtotal/Discount/Total summary, so repeating it here only
                invited the reader to reconcile two numbers. Fixed-amount codes
                have no such split — their face value IS the money. */}
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <p className="truncate text-sm font-medium text-green-900">
                {applied.isPerk
                  ? t('customerDiscount.perkApplied', { name: applied.name })
                  : t('customerDiscount.codeApplied', { code: applied.code })}
              </p>
              <p className="flex items-baseline gap-1 text-sm text-green-700">
                {applied.discountType === 'percent' &&
                applied.discountValue !== undefined ? (
                  <span>
                    {t('customerDiscount.offers.percentOff', {
                      value: applied.discountValue.toFixed(0),
                    })}
                  </span>
                ) : (
                  <>
                    <span>−</span>
                    <CurrencyIcon className="h-3 w-3 self-center" />
                    <span>{applied.amountOff.toFixed(2)}</span>
                  </>
                )}
                {applied.isPerk && (
                  <span>· {t('customerDiscount.memberDiscount')}</span>
                )}
              </p>
            </div>
          </div>
          {!applied.isPerk && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={t('customerDiscount.removeAria', {
                code: applied.code,
              })}
              disabled={isPending}
              onClick={handleRemove}
              // h-7 w-7 overrides the size="sm" default height, which would
              // otherwise set the chip's minimum height and cancel out the
              // reduced vertical padding above.
              className="h-7 w-7 shrink-0 p-0 text-green-900 hover:bg-green-100 hover:text-green-950"
              data-test="remove-discount-code"
            >
              {isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!isOpen) {
    return (
      <div className={cn('space-y-2', className)}>
        {suggestionButtons}
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="text-muted-foreground hover:bg-muted/50 flex w-full items-center justify-between rounded-lg border border-dashed px-3 py-2 text-sm transition-colors"
          data-test="open-discount-code-input"
        >
          <span className="flex items-center gap-2">
            <Tag className="h-4 w-4" />
            {t('customerDiscount.haveCode')}
          </span>
          <span className="text-xs">+</span>
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'bg-background space-y-2 rounded-lg border px-3 py-3',
        className,
      )}
      data-test="discount-code-input"
    >
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm font-medium">
          <Tag className="h-4 w-4" />
          {t('customerDiscount.label')}
        </label>
        <button
          type="button"
          aria-label={t('customerDiscount.close')}
          onClick={() => {
            setIsOpen(false);
            setError(null);
            setCode('');
          }}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {suggestionButtons}

      <div className="flex gap-2">
        <Input
          type="text"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleApply();
            }
          }}
          placeholder={t('customerDiscount.placeholder')}
          autoComplete="off"
          autoCapitalize="characters"
          disabled={isPending}
          className="uppercase"
          data-test="discount-code-field"
        />
        <Button
          type="button"
          onClick={handleApply}
          disabled={isPending || !code.trim()}
          data-test="apply-discount-code"
        >
          {isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            t('customerDiscount.apply')
          )}
        </Button>
      </div>

      {error && (
        <p
          className="text-destructive flex items-center gap-1 text-xs"
          data-test="discount-code-error"
        >
          <AlertCircle className="h-3 w-3 flex-shrink-0" />
          {error}
        </p>
      )}

      {showCancelNote && (
        <p className="text-muted-foreground text-xs">
          {t('customerDiscount.cancelNote')}
        </p>
      )}
    </div>
  );
}
